import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseApi, CourseDetails, Unit, Lesson } from '../../hooks/useCourseApi';
import { Curriculum } from './Curriculum';
import { checkEnrollment } from '../../services/enrollmentService';
import { BASE_URL } from '../../apiConfig';
import axios from 'axios';
import { getTenantHeaders } from '../../config/tenant';
import UnitEnrollmentPopup from './UnitEnrollmentPopup';
import { ArrowRight, BookOpen, CheckCircle, MessageCircle, Play } from 'lucide-react';

const getGradeInArabic = (grade: string): string => {
  const gradeTranslations: Record<string, string> = {
    Secondary1: 'أولى ثانوي',
    Secondary2: 'ثانية ثانوي',
    Secondary3: 'ثالثة ثانوي',
    default: grade,
  };
  return gradeTranslations[grade] || grade;
};

const getTermInArabic = (term: string): string => {
  const terms: Record<string, string> = {
    First: 'الفصل الأول',
    Second: 'الفصل الثاني',
    Summer: 'الفصل الصيفي',
  };
  return terms[term] || term;
};

interface ContactInfo {
  id: number;
  whatsApp_Number: string;
  facebook_Page: string;
  youTube_Channel: string;
  tiktokChannel: string;
}

interface ContactApiResponse {
  success: boolean;
  message: string;
  data: ContactInfo[];
}

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const { fetchCourseDetailsWithoutProgress, isLoading, error, enrollInCourse } = useCourseApi();

  const getCourseImageSrc = (details: CourseDetails) => {
    if (!details.imagePath) return '/default-course.jpg';
    if (details.imagePath.startsWith('http')) return details.imagePath;
    let imagePath = details.imagePath.startsWith('/')
      ? details.imagePath.substring(1)
      : details.imagePath;
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
    return `${baseUrl}${imagePath}`;
  };

  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(true);
  const [code, setCode] = useState('');
  const [enrollmentError, setEnrollmentError] = useState('');
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showUnitEnrollmentPopup, setShowUnitEnrollmentPopup] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitEnrollmentStatus, setUnitEnrollmentStatus] = useState<Map<number, boolean>>(new Map());

  const setUnitEnrollmentFromCourseStatus = useCallback(() => {
    if (!courseDetails) return;
    const enrollmentMap = new Map<number, boolean>();
    courseDetails.units.forEach((unit) => {
      enrollmentMap.set(unit.id, isEnrolled);
    });
    setUnitEnrollmentStatus(enrollmentMap);
  }, [courseDetails, isEnrolled]);

  useEffect(() => {
    const loadCourseDetails = async () => {
      if (id) {
        const response = await fetchCourseDetailsWithoutProgress(id);
        if (response?.success) setCourseDetails(response.data);
      }
    };
    loadCourseDetails();
  }, [id, fetchCourseDetailsWithoutProgress]);

  useEffect(() => {
    const checkUserEnrollment = async () => {
      if (id && currentUser?.id) {
        setIsCheckingEnrollment(true);
        const enrolled = await checkEnrollment(currentUser.id, id);
        setIsEnrolled(enrolled);
        setIsCheckingEnrollment(false);
      } else {
        setIsCheckingEnrollment(false);
      }
    };
    checkUserEnrollment();
  }, [id, currentUser?.id]);

  useEffect(() => {
    if (courseDetails && !isCheckingEnrollment) {
      setUnitEnrollmentFromCourseStatus();
    }
  }, [courseDetails, isEnrolled, isCheckingEnrollment, setUnitEnrollmentFromCourseStatus]);

  useEffect(() => {
    const fetchWhatsAppNumber = async () => {
      try {
        const response = await axios.get<ContactApiResponse>(
          'https://api.ibrahim-magdy.com/api/Contact/getAll',
          { headers: { ...getTenantHeaders() } }
        );
        if (response.data.success && response.data.data.length > 0) {
          setWhatsappNumber(response.data.data[0].whatsApp_Number);
        }
      } catch (err) {
        console.error('Error fetching WhatsApp number:', err);
      }
    };
    fetchWhatsAppNumber();
  }, []);

  const handleContinue = () => navigate(`/course-player/${id}`);

  const handleWhatsAppContact = () => {
    if (whatsappNumber) {
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=مرحباً، أريد الاستفسار عن التسجيل في الدورة: ${courseDetails?.courseName}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setEnrollmentError('يرجى إدخال كود التسجيل');
      setEnrollmentSuccess(false);
      return;
    }
    if (!isAuthenticated) {
      setEnrollmentError('يرجى تسجيل الدخول للتسجيل في هذه الدورة');
      setEnrollmentSuccess(false);
      return;
    }
    try {
      setIsEnrolling(true);
      setEnrollmentError('');
      setEnrollmentSuccess(false);
      const response = await enrollInCourse(id || '', code);
      if (response.success && response.message !== 'Code not found.') {
        setEnrollmentSuccess(true);
        setEnrollmentError('');
        setShowSuccessPopup(true);
        if (id && currentUser?.id) {
          const enrolled = await checkEnrollment(currentUser.id, id);
          setIsEnrolled(enrolled);
          if (enrolled && courseDetails) {
            const enrollmentMap = new Map<number, boolean>();
            courseDetails.units.forEach((unit) => enrollmentMap.set(unit.id, true));
            setUnitEnrollmentStatus(enrollmentMap);
          }
        }
      } else {
        setEnrollmentSuccess(false);
        setEnrollmentError(response.message || 'كود التسجيل غير صحيح');
      }
    } catch {
      setEnrollmentSuccess(false);
      setEnrollmentError('حدث خطأ أثناء التسجيل في الدورة');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isEnrolling) handleCodeSubmit();
  };

  const handleStartCourse = () => {
    setShowSuccessPopup(false);
    navigate(`/course-player/${id}`);
  };

  const handleGoToMyCourses = () => {
    setShowSuccessPopup(false);
    navigate('/my-lectures');
  };

  const getUnitEnrollmentStatus = (unitId: number): boolean =>
    unitEnrollmentStatus.get(unitId) || false;

  const handleUnitClick = (unit: Unit) => {
    if (!currentUser?.id) {
      navigate('/login', { state: { redirectTo: `/courses/${id}` } });
      return;
    }
    if (getUnitEnrollmentStatus(unit.id)) {
      navigate(`/course-player/${id}`);
    } else {
      setSelectedUnit(unit);
      setShowUnitEnrollmentPopup(true);
    }
  };

  const handleUnitEnrollmentClose = () => {
    setShowUnitEnrollmentPopup(false);
    setSelectedUnit(null);
  };

  const handleLessonSelect = (lesson: Lesson, unit: Unit) => {
    if (!currentUser?.id) {
      navigate('/login', { state: { redirectTo: `/courses/${id}` } });
      return;
    }
    if (getUnitEnrollmentStatus(unit.id)) {
      navigate(`/course-player/${id}`);
    } else {
      setSelectedUnit(unit);
      setShowUnitEnrollmentPopup(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" dir="rtl">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-sage-200 border-t-primary-dark" />
          <p className="text-sm text-slate-500">جاري تحميل الدورة...</p>
        </div>
      </div>
    );
  }

  if (error || !courseDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4" dir="rtl">
        <div className="text-center">
          <p className="mb-4 text-slate-700">تعذر تحميل تفاصيل الدورة</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-dark hover:underline"
          >
            <ArrowRight className="h-4 w-4" />
            رجوع
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);
  const priceLabel =
    courseDetails.price === 0 ? 'مجاناً' : `${courseDetails.price.toFixed(2)} جنيه`;
  const gradeLabel = getGradeInArabic(courseDetails.grade);
  const meta = [
    gradeLabel !== courseDetails.grade ? gradeLabel : null,
    getTermInArabic(courseDetails.term),
    `${totalLessons} درس`,
    priceLabel,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white" dir="rtl" lang="ar">
      {showSuccessPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg">
            <div className="mb-5 text-center">
              <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
              <h3 className="mb-1 text-lg font-bold text-slate-900">تم التسجيل بنجاح</h3>
              <p className="text-sm text-slate-500">{courseDetails.courseName}</p>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={handleStartCourse}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
              >
                <Play className="h-4 w-4" />
                بدأ الدورة الآن
              </button>
              <button
                type="button"
                onClick={handleGoToMyCourses}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-sage-200 px-4 py-3 text-sm font-semibold text-slate-700"
              >
                <BookOpen className="h-4 w-4" />
                الانتقال الى باقاتي
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 pb-16 pt-24 sm:px-6 sm:pt-28">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-primary-dark"
        >
          <ArrowRight className="h-4 w-4" />
          رجوع
        </button>

        <img
          src={getCourseImageSrc(courseDetails)}
          alt={courseDetails.courseName}
          className="mb-6 aspect-[16/9] w-full rounded-2xl object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/default-course.jpg';
          }}
        />

        <h1 className="mb-3 text-2xl font-extrabold leading-snug text-slate-900 sm:text-3xl">
          {courseDetails.courseName}
        </h1>

        <p className="mb-5 text-sm text-slate-500 sm:text-base">
          {meta.join(' · ')}
        </p>

        {courseDetails.description && (
          <p className="mb-8 max-w-prose text-sm leading-8 text-slate-600 sm:text-base">
            {courseDetails.description}
          </p>
        )}

        <div className="mb-12 space-y-4">
          {isCheckingEnrollment ? (
            <p className="text-sm text-slate-500">جاري التحقق من التسجيل...</p>
          ) : isEnrolled ? (
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground sm:w-auto"
            >
              <Play className="h-4 w-4" />
              استمرار الدورة
            </button>
          ) : courseDetails.price === 0 ? (
            <button
              type="button"
              onClick={() => navigate(`/course-player/${id}`)}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground sm:w-auto"
            >
              <Play className="h-4 w-4" />
              بدأ الدورة المجانية
            </button>
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="أدخل كود التسجيل"
                  disabled={isEnrolling}
                  className={`min-h-12 flex-1 rounded-xl border bg-white px-4 text-sm text-slate-900 outline-none placeholder:text-slate-400 ${
                    enrollmentError
                      ? 'border-red-400 bg-red-50'
                      : 'border-sage-200 focus:border-primary-dark'
                  }`}
                />
                <button
                  type="button"
                  onClick={handleCodeSubmit}
                  disabled={isEnrolling}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {isEnrolling ? 'جاري التسجيل...' : 'تسجيل'}
                </button>
              </div>
              {enrollmentError && (
                <p className="text-sm text-red-600">{enrollmentError}</p>
              )}
              {enrollmentSuccess && (
                <p className="text-sm text-emerald-600">تم التسجيل بنجاح</p>
              )}
              <button
                type="button"
                onClick={handleWhatsAppContact}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-dark"
              >
                <MessageCircle className="h-4 w-4" />
                التواصل مع الإشراف
              </button>
            </>
          )}
        </div>

        <section>
          <h2 className="mb-4 text-lg font-bold text-slate-900">المنهج</h2>
          <Curriculum
            units={courseDetails.units}
            courseId={courseDetails.id}
            onUnitClick={handleUnitClick}
            onLessonSelect={handleLessonSelect}
          />
        </section>
      </div>

      {selectedUnit && (
        <UnitEnrollmentPopup
          isOpen={showUnitEnrollmentPopup}
          onClose={handleUnitEnrollmentClose}
          unitId={selectedUnit.id}
          unitName={selectedUnit.unitName}
        />
      )}
    </div>
  );
};

export default CourseDetail;
